import { Component } from 'preact'
import classNames from 'classnames'
import Axios from 'axios'

import ProductUpdateDateFormatter from '../../libs/translationsUtils/dateFormatter'
import Phrase from '../../libs/translationsUtils/phrase'
import TopicViewsCount from '../../control-components/ui-components/TopicViewsCount'

import TopicLabel from '../topic-list-view/TopicLabel'
import ClampLines from '../clamp-lines/ClampLines'
import htmlParser from '../utils/htmlParser'
import TopicLike from '../topic-list-view/TopicLike'
import { LikedUserType } from '../topic-list-view/LikedUsers'
import TopicComments from '../topic-list-view/TopicComments'
import TopicLastPost from '../topic-list-view/TopicLastPost'
import urlGenerator from '../utils/urlGenerator'
import { common } from '../common'

import { ProductUpdateType } from './Types'

interface Person {
  id: string
  avatar: string
  username: string
  reputation: {
    repliesMade: number
  }
}

interface Props extends ProductUpdateType {
  isGuest: boolean
  key: string
  id: string
  productAreasMapping: {
    [key: string]: string
  }
  currentUser?: LikedUserType
  likeSet: string[]
  replyCount: number
  lastPostId: number
  lastContributor: Person
  lastActivityAt: string
  publicId: string
}

interface State {
  isCurrentUserLiked: boolean
  likedUsers: {
    [key: string]: LikedUserType
  }
}

class ProductUpdateView extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      likedUsers: {},
      isCurrentUserLiked:
        !!this.props.currentUser &&
        this.props.currentUser.id !== null &&
        this.props.likeSet.includes(this.props.currentUser.id.toString())
    }
    this.handleLikeStatusToggle = this.handleLikeStatusToggle.bind(this)
  }

  async handleLikeStatusToggle(e: MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (!this.props.currentUser) return

    if (this.props.currentUser.id === null) {
      const event = new CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }

    if (this.state.isCurrentUserLiked) {
      delete this.state.likedUsers[this.props.currentUser.id]
      this.setState({ likedUsers: this.state.likedUsers, isCurrentUserLiked: false })
      await Axios.post(`/community/api/productUpdates/${this.props.id}/unlike`, {
        unlikedBy: this.props.currentUser.id
      })
    } else {
      this.setState({
        likedUsers: {
          ...this.state.likedUsers,
          [this.props.currentUser.id]: this.props.currentUser
        },
        isCurrentUserLiked: true
      })
      await Axios.post(`/community/api/productUpdates/${this.props.id}/like`, {
        likedBy: this.props.currentUser.id
      })
    }
  }

  async componentDidMount() {
    if (this.props.likeSet.length) {
      const res = await Axios.post('/member/getUsersById', {
        ids: this.props.likeSet
      })

      this.setState({ likedUsers: res.data })
    }
  }

  render() {
    const {
      content, contentType, views, featuredImage, productAreas, productAreasMapping, replyCount, lastActivityAt,
      lastContributor, lastPostId, publicId, id, title, publicLabel, publishedAt
    } = this.props

    const { likedUsers, isCurrentUserLiked } = this.state
    const strippedContent = htmlParser.stripHtml(content)
    const productUpdateUrl = urlGenerator('product-updates', title, publicId)
    const totalLikes = Object.keys(likedUsers).length
    const lastPostUrl = lastPostId
      ? urlGenerator('product-updates', title, publicId, null, lastPostId.toString())
      : productUpdateUrl
    const relativeDate = common.relativeDate(lastActivityAt)

    return (
      <article className='product-update-view card-widget-wrapper'>
        <a href={productUpdateUrl} className='product-update-url' aria-label={title}>
          {Boolean(featuredImage) && (
            <img className='product-update-view_feature-image' src={featuredImage} />
          )}
          <div className='product-update-view_info'>
            <div className='product-update-view_info--metadata'>
              <TopicLabel topic={{ contentType: contentType, publicLabel: publicLabel }} />
              <span className='text--meta'>
                <ProductUpdateDateFormatter date={publishedAt} />
              </span>
            </div>
            <h4 className={classNames('card-widget-title', 'product-update-view_info-title', 'h3')}>{title}</h4>
            <ClampLines
              id='product-update-view'
              className='card-widget-text product-update-view_info-content'
              innerElement='p'
              lines={4}
              text={strippedContent || ''}
            />
            {Boolean(productAreas.length) && (
              <div className='product-update-view_product-areas'>
                <span className='text--meta'>
                  <Phrase p='product.areas.related.products' />:
                </span>
                {productAreas.map((productAreaId) => (
                  <span key={productAreaId} className='text--dot-underline product-area-item'>
                    {productAreasMapping[productAreaId]}
                  </span>
                ))}
              </div>
            )}
            <div className='topic-view_footer'>
              <ul className='topic-view_footer-items'>
                <TopicLike
                  className={isCurrentUserLiked ? 'active' : ''}
                  onClick={this.handleLikeStatusToggle}
                  displayLikeCount
                  likeCount={totalLikes}
                />
                <li className={classNames('thread-meta-item', 'thread-meta-item--hover-highlight', 'is-hidden-S')}>
                  <a className='qa-topic-meta-views' href={productUpdateUrl}>
                    <TopicViewsCount views={views} />
                  </a>
                </li>
                <li className='thread-meta-item'>
                  <TopicComments numberOfReplies={replyCount} id={id} lastPostUrl={lastPostUrl} />
                </li>
                <li className='thread-meta-item'>
                  <TopicLastPost
                    lastPostAuthor={{ ...lastContributor, name: lastContributor.username }}
                    lastPostUrl={lastPostUrl}
                    lastPostRelativeCreationDate={relativeDate}
                  />
                </li>
              </ul>
            </div>
          </div>
        </a>
      </article>
    )
  }
}

export default ProductUpdateView
